import { commons, universal } from '@0xsequence/core';
import { signers } from '@0xsequence/signhub';
import { encodeTypedDataDigest } from '@0xsequence/utils';
import { ethers } from 'ethers';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

//
// Types
//

//
// Client
//
class Guard {
  constructor(hostname, fetch) {
    this.hostname = void 0;
    this.fetch = void 0;
    this.path = '/rpc/Guard/';
    this.ping = headers => {
      return this.fetch(this.url('Ping'), createHTTPRequest({}, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            status: _data.status
          };
        });
      });
    };
    this.version = headers => {
      return this.fetch(this.url('Version'), createHTTPRequest({}, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            version: _data.version
          };
        });
      });
    };
    this.runtimeStatus = headers => {
      return this.fetch(this.url('RuntimeStatus'), createHTTPRequest({}, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            status: _data.status
          };
        });
      });
    };
    this.getSignerConfig = (args, headers) => {
      return this.fetch(this.url('GetSignerConfig'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            signerConfig: _data.signerConfig
          };
        });
      });
    };
    this.sign = (args, headers) => {
      return this.fetch(this.url('Sign'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            sig: _data.sig
          };
        });
      });
    };
    this.signWith = (args, headers) => {
      return this.fetch(this.url('SignWith'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            sig: _data.sig
          };
        });
      });
    };
    this.authMethods = (args, headers) => {
      return this.fetch(this.url('AuthMethods'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            methods: _data.methods,
            active: _data.active
          };
        });
      });
    };
    this.setPIN = (args, headers) => {
      return this.fetch(this.url('SetPIN'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {};
        });
      });
    };
    this.resetPIN = (args, headers) => {
      return this.fetch(this.url('ResetPIN'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {};
        });
      });
    };
    this.createTOTP = (args, headers) => {
      return this.fetch(this.url('CreateTOTP'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            uri: _data.uri
          };
        });
      });
    };
    this.commitTOTP = (args, headers) => {
      return this.fetch(this.url('CommitTOTP'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            codes: _data.codes
          };
        });
      });
    };
    this.resetTOTP = (args, headers) => {
      return this.fetch(this.url('ResetTOTP'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {};
        });
      });
    };
    this.reset2FA = (args, headers) => {
      return this.fetch(this.url('Reset2FA'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {};
        });
      });
    };
    this.recoveryCodes = (args, headers) => {
      return this.fetch(this.url('RecoveryCodes'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            codes: _data.codes
          };
        });
      });
    };
    this.resetRecoveryCodes = (args, headers) => {
      return this.fetch(this.url('ResetRecoveryCodes'), createHTTPRequest(args, headers)).then(res => {
        return buildResponse(res).then(_data => {
          return {
            codes: _data.codes
          };
        });
      });
    };
    this.hostname = hostname;
    this.fetch = (input, init) => fetch(input, init);
  }
  url(name) {
    return this.hostname + this.path + name;
  }
}
const createHTTPRequest = (body = {}, headers = {}) => {
  return {
    method: 'POST',
    headers: _extends({}, headers, {
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(body || {})
  };
};
const buildResponse = res => {
  return res.text().then(text => {
    let data;
    try {
      data = JSON.parse(text);
    } catch (err) {
      throw {
        code: 'unknown',
        msg: `expecting JSON, got: ${text}`,
        status: res.status
      };
    }
    if (!res.ok) {
      throw data; // webrpc error response
    }
    return data;
  });
};

const fetch = typeof global === 'object' ? global.fetch : window.fetch;
class GuardSigner {
  constructor(address, url, appendSuffix = false) {
    this.address = address;
    this.url = url;
    this.appendSuffix = appendSuffix;
    this.guard = void 0;
    this.guard = new Guard(url, fetch);
  }
  async getAddress() {
    return this.address;
  }
  async buildDeployTransaction(_metadata) {
    return undefined;
  }
  async predecorateSignedTransactions(_metadata) {
    return [];
  }
  async decorateTransactions(bundle, _metadata) {
    return bundle;
  }
  async sign(message, metadata) {
    var _metadata$parts;
    if (!commons.isWalletSignRequestMetadata(metadata)) {
      throw new Error('expected sequence signature request metadata');
    }
    const guardTotpCode = metadata.guardTotpCode;

    // Building auxData, notice: this uses the old v1 format
    // TODO: We should update the guard API so we can pass the metadata directly
    const coder = universal.genericCoderFor(metadata.config.version);
    const {
      encoded
    } = coder.signature.encodeSigners(metadata.config, (_metadata$parts = metadata.parts) != null ? _metadata$parts : new Map(), [], metadata.chainId);
    return (await this.guard.signWith({
      signer: this.address,
      request: {
        msg: ethers.utils.hexlify(message),
        auxData: this.packMsgAndSig(metadata.address, metadata.digest, encoded, metadata.chainId),
        chainId: ethers.BigNumber.from(metadata.chainId).toNumber()
      },
      token: guardTotpCode ? {
        id: AuthMethod.TOTP,
        token: guardTotpCode
      } : undefined
    })).sig;
  }
  notifyStatusChange(_id, _status, _metadata) {}
  async getAuthMethods(proof) {
    let response;
    if ('jwt' in proof) {
      response = await this.guard.authMethods({}, {
        Authorization: `BEARER ${proof.jwt}`
      });
    } else {
      const signedProof = await signOwnershipProof(proof);
      response = await this.guard.authMethods({
        proof: {
          wallet: signedProof.walletAddress,
          timestamp: signedProof.timestamp.getTime(),
          signer: signedProof.signerAddress,
          signature: signedProof.signature
        }
      });
    }
    return _extends({}, response, {
      methods: response.methods.map(parseAuthMethod)
    });
  }
  async setPin(pin, proof) {
    const signedProof = await signAuthUpdateProof(proof);
    if (pin === undefined) {
      await this.guard.resetPIN({
        timestamp: signedProof.timestamp.getTime(),
        signature: signedProof.signature
      }, {
        Authorization: `BEARER ${proof.jwt}`
      });
    } else {
      await this.guard.setPIN({
        pin,
        timestamp: signedProof.timestamp.getTime(),
        signature: signedProof.signature
      }, {
        Authorization: `BEARER ${proof.jwt}`
      });
    }
  }
  resetPin(proof) {
    return this.setPin(undefined, proof);
  }
  async createTotp(proof) {
    const signedProof = await signAuthUpdateProof(proof);
    const {
      uri
    } = await this.guard.createTOTP({
      timestamp: signedProof.timestamp.getTime(),
      signature: signedProof.signature
    }, {
      Authorization: `BEARER ${proof.jwt}`
    });
    return new URL(uri);
  }
  async commitTotp(token, jwt) {
    const {
      codes
    } = await this.guard.commitTOTP({
      token
    }, {
      Authorization: `BEARER ${jwt}`
    });
    return codes;
  }
  async resetTotp(proof) {
    const signedProof = await signAuthUpdateProof(proof);
    await this.guard.resetTOTP({
      timestamp: signedProof.timestamp.getTime(),
      signature: signedProof.signature
    }, {
      Authorization: `BEARER ${proof.jwt}`
    });
  }
  async reset2fa(recoveryCode, proof) {
    if ('jwt' in proof) {
      await this.guard.reset2FA({
        code: recoveryCode
      }, {
        Authorization: `BEARER ${proof.jwt}`
      });
    } else {
      const signedProof = await signOwnershipProof(proof);
      await this.guard.reset2FA({
        code: recoveryCode,
        proof: {
          wallet: signedProof.walletAddress,
          timestamp: signedProof.timestamp.getTime(),
          signer: signedProof.signerAddress,
          signature: signedProof.signature
        }
      });
    }
  }
  async getRecoveryCodes(proof) {
    const signedProof = await signAuthUpdateProof(proof);
    const {
      codes
    } = await this.guard.recoveryCodes({
      timestamp: signedProof.timestamp.getTime(),
      signature: signedProof.signature
    }, {
      Authorization: `BEARER ${proof.jwt}`
    });
    return codes;
  }
  async resetRecoveryCodes(proof) {
    const signedProof = await signAuthUpdateProof(proof);
    const {
      codes
    } = await this.guard.resetRecoveryCodes({
      timestamp: signedProof.timestamp.getTime(),
      signature: signedProof.signature
    }, {
      Authorization: `BEARER ${proof.jwt}`
    });
    return codes;
  }
  packMsgAndSig(address, msg, sig, chainId) {
    return ethers.utils.defaultAbiCoder.encode(['address', 'uint256', 'bytes', 'bytes'], [address, chainId, msg, sig]);
  }
  suffix() {
    return this.appendSuffix ? [3] : [];
  }
}
let AuthMethod = /*#__PURE__*/function (AuthMethod) {
  AuthMethod["PIN"] = "PIN";
  AuthMethod["TOTP"] = "TOTP";
  return AuthMethod;
}({});
function parseAuthMethod(method) {
  switch (method) {
    case AuthMethod.PIN:
    case AuthMethod.TOTP:
      return method;
    default:
      throw new Error(`unknown auth method '${method}'`);
  }
}
function isSignedOwnershipProof(proof) {
  return 'signerAddress' in proof && typeof proof.signerAddress === 'string';
}
async function signOwnershipProof(proof) {
  if (isSignedOwnershipProof(proof)) {
    return proof;
  } else {
    const signer = signers.isSapientSigner(proof.signer) ? proof.signer : new signers.SignerWrapper(proof.signer);
    const signerAddress = await signer.getAddress();
    const timestamp = new Date();
    const typedData = getOwnershipProofTypedData(proof.walletAddress, timestamp);
    const digest = encodeTypedDataDigest(typedData);
    return {
      walletAddress: proof.walletAddress,
      timestamp,
      signerAddress,
      signature: ethers.utils.hexlify(await signer.sign(digest, {}))
    };
  }
}
async function signAuthUpdateProof(proof) {
  if ('wallet' in proof) {
    var _typedData$domain$cha;
    const timestamp = new Date();
    const typedData = getAuthUpdateProofTypedData(timestamp);
    const signature = await proof.wallet.signTypedData(typedData.domain, typedData.types, typedData.message, (_typedData$domain$cha = typedData.domain.chainId) != null ? _typedData$domain$cha : 1, 'eip6492');
    return {
      jwt: proof.jwt,
      timestamp,
      signature
    };
  } else {
    return proof;
  }
}
function getOwnershipProofTypedData(wallet, timestamp) {
  return {
    domain,
    types: {
      AuthMethods: [{
        name: 'wallet',
        type: 'address'
      }, {
        name: 'timestamp',
        type: 'string'
      }]
    },
    message: {
      wallet: ethers.utils.getAddress(wallet),
      timestamp: toUTCString(timestamp)
    }
  };
}
function getAuthUpdateProofTypedData(timestamp) {
  return {
    domain,
    types: {
      AuthUpdate: [{
        name: 'timestamp',
        type: 'string'
      }]
    },
    message: {
      timestamp: toUTCString(timestamp)
    }
  };
}
const domain = {
  name: 'Sequence Guard',
  version: '1',
  chainId: 1
};
function toUTCString(date) {
  return date.toUTCString().replace('GMT', 'UTC');
}

export { AuthMethod, GuardSigner, getAuthUpdateProofTypedData, getOwnershipProofTypedData };
